import { useEffect } from 'react';
import { toast } from '../utils/toast.js';
import { getMessage } from '../i18n/messages.js';
import { sanitizeAccountReturnTo } from '../utils/accountRecovery.js';
import { clearPendingSocialState, clearSocialLoginPopupFlow, consumeSocialLoginContext, getProviderLabel, markSocialNotLinked, notifySocialLoginPopup, parseSocialProviderId, readPendingTarget, readSocialLoginPopupFlow, readPendingProvider, } from '../utils/socialAuth.js';
import { readCheckoutResume } from '../utils/checkoutResume.js';
export function useSocialAuthCallback({ defaultRemember = true, onLoginSuccess, onNotLinked, onMfaRequired, }) {
    useEffect(() => {
        const { search, pathname } = window.location;
        if (search) {
            const q = new URLSearchParams(search);
            const err = q.get('error');
            const provider = parseSocialProviderId(q.get('provider'));
            if (err) {
                const providerLabel = getProviderLabel(provider);
                if (err === 'not-linked') {
                    // Booking-gate origin: when the attempt came from inside checkout (a
                    // checkout-resume marker is present) AND this is the full-page redirect
                    // return — not a popup, which carries the signal in-memory via
                    // BroadcastChannel — persist the not-linked marker and route the booker
                    // back to the listing so the gate reopens and renders the not-linked
                    // card in-flow, instead of stranding them on the standalone /login page.
                    const popupFlow = readSocialLoginPopupFlow();
                    if (provider && !popupFlow && readCheckoutResume()) {
                        markSocialNotLinked(provider);
                        window.location.assign(readPendingTarget() ?? '/');
                        return;
                    }
                    if (provider) {
                        onNotLinked(provider);
                        toast.info(`${providerLabel} account not linked. Sign up to create an account or sign in with email.`);
                    }
                    else {
                        toast.info('Social provider account not linked. Sign in with email to continue.');
                    }
                }
                else if (err === 'oauth-failed') {
                    toast.error(`${providerLabel} authentication failed. Please try again.`);
                }
                else {
                    toast.error('Authentication failed. Please try again.');
                }
                const popupProvider = readSocialLoginPopupFlow();
                if (popupProvider) {
                    notifySocialLoginPopup({
                        flowId: popupProvider.flowId,
                        flowToken: popupProvider.flowToken,
                        status: 'complete',
                        ok: false,
                        provider: popupProvider.provider,
                        mode: popupProvider.mode,
                        reason: err === 'not-linked' ? 'not-linked' : 'oauth-failed',
                        error: err === 'not-linked'
                            ? `${providerLabel} account not linked. Sign in with email or create an account.`
                            : `${providerLabel} authentication failed.`,
                    });
                    clearSocialLoginPopupFlow();
                }
                // Clear the query string to avoid repeating the message on reload
                window.history.replaceState(null, '', pathname);
                return; // do not proceed to hash handling in the same tick
            }
        }
        const { hash, pathname: path } = window.location;
        if (!hash) {
            return;
        }
        const params = new URLSearchParams(hash.substring(1));
        const accessToken = params.get('accessToken');
        const refreshToken = params.get('refreshToken');
        const jti = params.get('jti');
        const oauthError = params.get('error');
        const mfaRequired = params.get('mfaRequired');
        const challengeId = params.get('challengeId') ?? params.get('sessionId') ?? params.get('mfaChallengeId');
        if (oauthError && !accessToken) {
            window.history.replaceState(null, '', path);
            const pending = readPendingProvider();
            const providerLabel = getProviderLabel(pending);
            toast.error(`${providerLabel} is not linked to your account yet. Sign in with your email and we'll finish linking it.`);
            const popupProvider = readSocialLoginPopupFlow();
            if (popupProvider) {
                notifySocialLoginPopup({
                    flowId: popupProvider.flowId,
                    flowToken: popupProvider.flowToken,
                    status: 'complete',
                    ok: false,
                    provider: popupProvider.provider,
                    mode: popupProvider.mode,
                    reason: 'not-linked',
                    error: `${providerLabel} is not linked to your account yet.`,
                });
                clearSocialLoginPopupFlow();
            }
            return;
        }
        if ((mfaRequired === '1' || mfaRequired === 'true' || challengeId) && challengeId) {
            const paramsSearch = new URLSearchParams(window.location.search);
            const { remember, returnTo } = consumeSocialLoginContext(defaultRemember);
            window.history.replaceState(null, '', path);
            const queryReturnTo = paramsSearch.get('returnTo');
            const target = sanitizeAccountReturnTo(returnTo ?? queryReturnTo);
            const challenge = {
                challengeId,
                userId: params.get('userId'),
            };
            const deliveryMethod = params.get('deliveryMethod') ?? params.get('method');
            if (deliveryMethod) {
                challenge.deliveryMethod = deliveryMethod;
            }
            const deliveryHint = params.get('destination') ??
                params.get('maskedDestination') ??
                params.get('phone') ??
                params.get('email');
            if (deliveryHint) {
                challenge.deliveryHint = deliveryHint;
            }
            onMfaRequired?.({
                challenge,
                remember,
                target,
            });
            return;
        }
        if (accessToken) {
            const paramsSearch = new URLSearchParams(window.location.search);
            const { remember, returnTo } = consumeSocialLoginContext(defaultRemember);
            window.history.replaceState(null, '', path);
            toast.success(getMessage('en-US', 'loginSuccess'));
            const queryReturnTo = paramsSearch.get('returnTo');
            const target = sanitizeAccountReturnTo(returnTo ?? queryReturnTo);
            clearPendingSocialState();
            onLoginSuccess({
                accessToken,
                refreshToken: refreshToken ?? undefined,
                jti: jti ?? undefined,
                remember,
                target,
            });
        }
    }, [defaultRemember, onLoginSuccess, onMfaRequired, onNotLinked]);
}
//# sourceMappingURL=useSocialAuthCallback.js.map